import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from 'react-query';
import Select from 'react-select';
import { getAllTags, findOrCreateTag } from '../services/TagService';

/**
 * Componente TagSelect que permite seleccionar múltiples tags y crear nuevos si no existen
 * Similar al input de idiomas pero con capacidad de crear tags automáticamente
 * 
 * @param {Object} props
 * @param {string|Array} props.value - Valor actual (string de IDs separados por comas o array de objetos {id, nombre})
 * @param {Function} props.onChange - Callback cuando cambia la selección (recibe array de objetos {id, nombre})
 * @param {string} props.tipo - Tipo de tag (TagTipo) requerido para crear nuevos tags
 * @param {string} props.placeholder - Placeholder del select
 * @param {boolean} props.isDisabled - Si el select está deshabilitado
 * @param {string} props.className - Clases CSS adicionales
 * @param {string} props.error - Mensaje de error a mostrar
 */
export function TagSelect({
    value,
    onChange,
    tipo,
    placeholder = "Selecciona o crea tags...",
    isDisabled = false,
    className = "",
    error = null
}) {
    const [isCreating, setIsCreating] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [menuIsOpen, setMenuIsOpen] = useState(false);

    // Query para obtener todos los tags (filtrados por tipo si se proporciona)
    const { data: tagsData, isLoading: isLoadingTags, refetch: refetchTags } = useQuery(
        ['tags', tipo],
        async () => {
            const response = await getAllTags(tipo);
            return response.data;
        }
    );

    // Convertir tags a formato para react-select
    const tagOptions = useMemo(() => {
        if (!tagsData) return [];
        return tagsData.map(tag => ({
            label: tag.nombre,
            value: tag.id,
            tag: tag // Guardar el objeto completo para referencia
        }));
    }, [tagsData]);

    // Convertir el valor actual a formato para react-select
    const currentValue = useMemo(() => {
        if (!value) return [];

        // Si value es un string (IDs separados por comas)
        if (typeof value === 'string') {
            const ids = value.split(',').map(id => id.trim()).filter(id => id);
            return tagOptions.filter(opt => ids.includes(opt.value));
        }

        // Si value es un array de objetos {id, nombre} o {id} o solo {nombre}
        if (Array.isArray(value)) {
            return value.map(item => {
                // Si el item es un objeto
                if (typeof item === 'object') {
                    // Si tiene ID, buscar por ID
                    if (item.id) {
                        const existingOption = tagOptions.find(opt => opt.value === item.id);
                        if (existingOption) return existingOption;
                    }

                    // Si tiene nombre pero no ID, buscar por nombre
                    if (item.nombre) {
                        const existingOption = tagOptions.find(
                            opt => opt.label.toLowerCase() === item.nombre.toLowerCase()
                        );
                        if (existingOption) return existingOption;
                    }

                    // Si no se encuentra, crear una opción temporal
                    return {
                        label: item.nombre || item.label || `Tag ${item.id || ''}`,
                        value: item.id || `__temp__${item.nombre}`,
                        tag: item
                    };
                }

                // Si el item es un string o número, tratarlo como ID
                const id = item;
                const existingOption = tagOptions.find(opt => opt.value === id);
                if (existingOption) return existingOption;

                return {
                    label: `Tag ${id}`,
                    value: id,
                    tag: { id }
                };
            });
        }

        return [];
    }, [value, tagOptions]);

    // Crear opción para "Crear nuevo tag"
    const createOption = useMemo(() => {
        if (!inputValue.trim() || !tipo) return null;

        // Verificar si ya existe un tag con ese nombre
        const exists = tagOptions.some(
            opt => opt.label.toLowerCase() === inputValue.trim().toLowerCase()
        );

        if (exists) return null;

        return {
            label: `Crear "${inputValue.trim()}"`,
            value: `__create__${inputValue.trim()}`,
            isCreateOption: true
        };
    }, [inputValue, tagOptions, tipo]);

    // Opciones combinadas (tags existentes + opción de crear)
    const allOptions = useMemo(() => {
        const options = [...tagOptions];
        if (createOption) {
            options.push(createOption);
        }
        return options;
    }, [tagOptions, createOption]);

    // Manejar creación de nuevo tag
    const handleCreateTag = useCallback(async (tagName) => {
        if (!tagName.trim() || !tipo) return;

        setIsCreating(true);
        try {
            // Buscar o crear el tag
            const response = await findOrCreateTag(tagName.trim(), tipo);
            const newTag = response.data;

            if (newTag) {
                // Refrescar la lista de tags
                await refetchTags();

                // Agregar el nuevo tag a la selección actual
                const newOption = {
                    label: newTag.nombre,
                    value: newTag.id,
                    tag: newTag
                };

                const updatedValue = [...currentValue, newOption];

                // Notificar al componente padre
                if (onChange) {
                    onChange(updatedValue.map(opt => ({
                        id: opt.value,
                        nombre: opt.label
                    })));
                }
            }
        } catch (error) {
            console.error('Error al crear tag:', error);
            // Mostrar mensaje de error
            alert(error.response?.data?.message || 'Error al crear el tag');
        } finally {
            setIsCreating(false);
            setInputValue('');
        }
    }, [tipo, currentValue, onChange, refetchTags]);

    // Manejar cambio en la selección
    const handleChange = useCallback((selectedOptions) => {
        const selected = selectedOptions || [];

        // Filtrar opciones de creación y manejar creación
        const regularOptions = [];
        const createOptions = [];

        selected.forEach(opt => {
            if (opt && opt.isCreateOption) {
                createOptions.push(opt);
            } else if (opt) {
                regularOptions.push(opt);
            }
        });

        // Si hay opciones de creación, crear los tags primero
        if (createOptions.length > 0) {
            createOptions.forEach(opt => {
                const tagName = opt.value.replace('__create__', '');
                handleCreateTag(tagName);
            });
        }

        // Actualizar con las opciones regulares (incluso si está vacío para permitir eliminar todas)
        if (onChange) {
            onChange(regularOptions.map(opt => ({
                id: opt.value,
                nombre: opt.label
            })));
        }
    }, [onChange, handleCreateTag]);

    // Manejar cuando el usuario escribe
    const handleInputChange = useCallback((newValue, actionMeta) => {
        if (actionMeta.action === 'input-change') {
            setInputValue(newValue);
        }
    }, []);

    // Manejar cuando el usuario presiona Enter
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Enter' && inputValue.trim() && createOption) {
            event.preventDefault();
            handleCreateTag(inputValue.trim());
        }
    }, [inputValue, createOption, handleCreateTag]);

    // Filtrar opciones basado en el input
    const filterOption = useCallback((option, inputValue) => {
        if (!inputValue) return true;
        if (option.isCreateOption) return true;
        return option.label.toLowerCase().includes(inputValue.toLowerCase());
    }, []);

    return (
        <>
            <Select
                value={currentValue}
                onChange={handleChange}
                options={allOptions}
                isMulti
                isSearchable
                isDisabled={isDisabled || isLoadingTags || isCreating}
                isLoading={isLoadingTags || isCreating}
                placeholder={isCreating ? "Creando tag..." : placeholder}
                className={className || "basic-multi-select"}
                classNamePrefix="select"
                onInputChange={handleInputChange}
                inputValue={inputValue}
                onKeyDown={handleKeyDown}
                filterOption={filterOption}
                menuIsOpen={menuIsOpen}
                onMenuOpen={() => setMenuIsOpen(true)}
                onMenuClose={() => setMenuIsOpen(false)}
                maxMenuHeight={180}
                styles={{
                    menu: (provided) => ({
                        ...provided,
                        maxHeight: '180px',
                        overflow: 'hidden'
                    }),
                    menuList: (provided) => ({
                        ...provided,
                        maxHeight: '180px',
                        overflowY: 'auto'
                    })
                }}
                noOptionsMessage={({ inputValue }) => {
                    if (!inputValue) {
                        return "Escribe para buscar o crear un tag";
                    }
                    if (createOption) {
                        return `Presiona Enter o selecciona "Crear" para crear "${inputValue}"`;
                    }
                    return "No se encontraron tags";
                }}
            />
            {error && (
                <div className="invalid-feedback d-block">{error}</div>
            )}
        </>
    );
}
